import create from 'zustand';

import { markInstalled } from '../pwa/pwa-install-storage';

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

type PwaState = {
  installPromptEvent: BeforeInstallPromptEvent | null;
  isInstallBannerVisible: boolean;
  isInstallable: boolean;
  isInstalled: boolean;
  isPwaOffline: boolean;
};

type PwaActions = {
  setInstallPromptEvent: (event: BeforeInstallPromptEvent | null) => void;
  setIsInstallable: (value: boolean) => void;
  setIsInstallBannerVisible: (value: boolean) => void;
  setIsInstalled: (value: boolean) => void;
  setIsPwaOffline: (value: boolean) => void;
  triggerInstallPrompt: () => Promise<'accepted' | 'dismissed' | null>;
};

export const usePwaStore = create<PwaState & PwaActions>()((set, get) => ({
  installPromptEvent: null,
  isInstallBannerVisible: false,
  isInstallable: false,
  isInstalled: false,
  isPwaOffline: false,
  setInstallPromptEvent: (event) =>
    set(() => ({ installPromptEvent: event, isInstallable: event !== null })),
  setIsInstallable: (value) => set(() => ({ isInstallable: value })),
  setIsInstallBannerVisible: (value) =>
    set(() => ({ isInstallBannerVisible: value })),
  setIsInstalled: (value) =>
    set((state) => ({
      isInstalled: value,
      isInstallable: value ? false : state.isInstallable,
    })),
  setIsPwaOffline: (value) => set(() => ({ isPwaOffline: value })),
  triggerInstallPrompt: async () => {
    const { installPromptEvent } = get();
    if (!installPromptEvent) return null;

    await installPromptEvent.prompt();
    const { outcome } = await installPromptEvent.userChoice;

    if (outcome === 'accepted') {
      markInstalled();
    }

    set((state) => ({
      isInstalled: outcome === 'accepted' ? true : state.isInstalled,
      isInstallable: false,
      installPromptEvent: null,
    }));

    return outcome;
  },
}));
